/**
 * LiveJobsTicker — homepage rolling strip of the 3 newest published jobs.
 *
 * Rendered above the fold on `/` so a visitor sees real activity, not just
 * marketing copy. Uses a CSS keyframe (see index.css) to slowly scroll the
 * strip; pauses on hover. Silently renders nothing when the API returns 0
 * jobs — no fake activity, no empty state noise.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, MapPin, DollarSign } from "lucide-react";
import api from "@/lib/api";
import { formatJobPay, payToneForJob } from "@/lib/payDisplay";

const timeAgo = (iso) => {
    if (!iso) return "";
    try {
        const diff = (Date.now() - new Date(iso).getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
        return `${Math.round(diff / 86400)}d ago`;
    } catch { return ""; }
};

const formatSalary = (job) => formatJobPay(job) || "";

const JobPill = ({ job, idx }) => {
    const salary = formatSalary(job);
    const payTone = payToneForJob(job);
    const to = job.slug ? `/jobs/${job.slug}` : `/jobs/${job.id}`;
    return (
        <Link
            to={to}
            data-testid={`home-jobs-ticker-item-${idx}`}
            className="group inline-flex items-center gap-3 rounded-full border border-border bg-card px-4 py-2 hover:border-emerald-500/50 hover:bg-accent transition-colors shrink-0"
        >
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Hiring
            </span>
            <span className="text-sm font-semibold text-foreground whitespace-nowrap">{job.title}</span>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
                {job.company}
            </span>
            {job.location && (
                <span className="hidden sm:inline-flex items-center gap-0.5 text-[11px] text-muted-foreground whitespace-nowrap">
                    <MapPin size={10} /> {job.location}
                </span>
            )}
            {salary && (
                <span
                    data-testid={`home-job-pay-${job.id}`}
                    data-pay-tone={payTone}
                    className={`hidden md:inline-flex items-center gap-0.5 text-[11px] whitespace-nowrap px-2 py-0.5 rounded-full ${payTone === "doe"
                        ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30"
                        : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30"}`}
                >
                    <DollarSign size={10} /> {salary}
                </span>
            )}
            <span className="text-[10px] text-muted-foreground/70 whitespace-nowrap">
                {timeAgo(job.posted_at)}
            </span>
        </Link>
    );
};

export default function LiveJobsTicker() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        api.get("/jobs/recent", { params: { limit: 3 } })
            .then((r) => { if (mounted) setJobs(r.data?.jobs || []); })
            .catch(() => { if (mounted) setJobs([]); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, []);

    if (loading) return null;
    if (!jobs.length) return null;

    // Duplicate the list so the CSS marquee loops seamlessly.
    const strip = [...jobs, ...jobs];

    return (
        <section
            className="relative border-y border-border bg-muted/30 py-3 overflow-hidden"
            aria-label="Recently posted jobs"
            data-testid="home-jobs-ticker"
        >
            <div className="container-page flex items-center gap-3">
                <div className="hidden md:inline-flex items-center gap-1 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <Briefcase size={12} /> Live jobs
                </div>
                <div className="relative flex-1 overflow-hidden group">
                    <div
                        className="flex items-center gap-3 animate-[marquee_28s_linear_infinite] group-hover:[animation-play-state:paused]"
                        style={{ willChange: "transform" }}
                        data-testid="home-jobs-ticker-strip"
                    >
                        {strip.map((job, i) => (
                            <JobPill key={`${job.id}-${i}`} job={job} idx={i % jobs.length} />
                        ))}
                    </div>
                    {/* Fade edges so the strip doesn't hard-cut visually. */}
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-muted/60 to-transparent" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-muted/60 to-transparent" />
                </div>
                <Link
                    to="/jobs"
                    data-testid="home-jobs-ticker-all-link"
                    className="hidden sm:inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-background hover:bg-accent px-3 py-1.5 text-xs font-semibold text-foreground transition-colors"
                >
                    See all jobs →
                </Link>
            </div>
        </section>
    );
}
